// ─────────────────────────────────────────────────────────────────────────────
// IndexedDB layer for offline (PWA) operation.
//
// Every store below backs one of the pages that must work fully offline:
// medicines, inventory, purchases, sales, dashboard, accounting.
// ─────────────────────────────────────────────────────────────────────────────

const DB_NAME = 'pharmacare-offline-v1';
const DB_VERSION = 5;

export const STORES = {
  medicines: 'medicines',       // POS / search cache (one row per medicine or medicine+batch)
  inventory: 'inventory',       // InventorySummary rows shown on the Inventory page
  inventoryTx: 'inventoryTx',   // local log of stock-in / stock-out / receive movements
  customers: 'customers',
  suppliers: 'suppliers',
  categories: 'categories',
  sales: 'sales',
  saleReturns: 'saleReturns',
  purchases: 'purchases',
  expenses: 'expenses',
  payments: 'payments',         // customer/supplier ledger payments
  settings: 'settings',
  queue: 'syncQueue',
  apiCache: 'apiCache',         // generic last-known GET responses (every page without a dedicated store)
  meta: 'meta',
};

let dbPromise;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORES.medicines)) {
        const s = db.createObjectStore(STORES.medicines, { keyPath: 'localKey' });
        s.createIndex('medicineId', 'medicineId', { unique: false });
        s.createIndex('barcode', 'barcode', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.inventory)) {
        const s = db.createObjectStore(STORES.inventory, { keyPath: '_id' });
        s.createIndex('medicineId', 'medicineId', { unique: false });
        s.createIndex('batchNumber', 'batchNumber', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.inventoryTx)) {
        const s = db.createObjectStore(STORES.inventoryTx, { keyPath: 'id', autoIncrement: true });
        s.createIndex('createdAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.customers)) {
        const s = db.createObjectStore(STORES.customers, { keyPath: '_id' });
        s.createIndex('name', 'name', { unique: false });
        s.createIndex('phone', 'phone', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.suppliers)) {
        const s = db.createObjectStore(STORES.suppliers, { keyPath: '_id' });
        s.createIndex('name', 'name', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.categories)) {
        db.createObjectStore(STORES.categories, { keyPath: '_id' });
      }
      if (!db.objectStoreNames.contains(STORES.sales)) {
        const s = db.createObjectStore(STORES.sales, { keyPath: '_id' });
        s.createIndex('createdAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.saleReturns)) {
        const s = db.createObjectStore(STORES.saleReturns, { keyPath: '_id' });
        s.createIndex('createdAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.purchases)) {
        const s = db.createObjectStore(STORES.purchases, { keyPath: '_id' });
        s.createIndex('createdAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.expenses)) {
        const s = db.createObjectStore(STORES.expenses, { keyPath: '_id' });
        s.createIndex('date', 'date', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.payments)) {
        const s = db.createObjectStore(STORES.payments, { keyPath: 'id', autoIncrement: true });
        s.createIndex('parentId', 'parentId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.settings)) {
        db.createObjectStore(STORES.settings, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORES.queue)) {
        const s = db.createObjectStore(STORES.queue, { keyPath: 'id', autoIncrement: true });
        s.createIndex('createdAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.apiCache)) {
        db.createObjectStore(STORES.apiCache, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORES.meta)) {
        db.createObjectStore(STORES.meta, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      // When a newer version of the app (another tab / after an update) wants
      // to upgrade the database, let go of it. An old tab that keeps the
      // connection open blocks the upgrade, and then EVERY offline read/write
      // in the new tab waits forever.
      db.onversionchange = () => { db.close(); dbPromise = null; };
      resolve(db);
    };
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

const tx = async (storeName, mode, work) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    let result;
    try { result = work(store, transaction); } catch (e) { reject(e); return; }
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
  });
};

// ── Generic primitives (usable against any store) ──────────────────────────
export async function putMany(storeName, rows) {
  if (!rows?.length) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(storeName, 'readwrite');
    const s = t.objectStore(storeName);
    rows.forEach(row => s.put(row));
    t.oncomplete = resolve;
    t.onerror = () => reject(t.error);
  });
}

export async function put(storeName, row) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(storeName, 'readwrite');
    const req = t.objectStore(storeName).put(row);
    t.oncomplete = () => resolve(req.result);
    t.onerror = () => reject(t.error);
  });
}

export async function get(storeName, key) {
  return tx(storeName, 'readonly', store => new Promise((resolve, reject) => {
    const r = store.get(key);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  }));
}

export async function getAll(storeName) {
  return tx(storeName, 'readonly', store => new Promise((resolve, reject) => {
    const r = store.getAll();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror = () => reject(r.error);
  }));
}

export async function getAllByIndex(storeName, indexName, value) {
  return tx(storeName, 'readonly', store => new Promise((resolve, reject) => {
    const r = store.index(indexName).getAll(value);
    r.onsuccess = () => resolve(r.result || []);
    r.onerror = () => reject(r.error);
  }));
}

export async function remove(storeName, key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(storeName, 'readwrite');
    t.objectStore(storeName).delete(key);
    t.oncomplete = resolve;
    t.onerror = () => reject(t.error);
  });
}

export async function clear(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(storeName, 'readwrite');
    t.objectStore(storeName).clear();
    t.oncomplete = resolve;
    t.onerror = () => reject(t.error);
  });
}

export async function setMeta(key, value) { return put(STORES.meta, { key, value, updatedAt: Date.now() }); }
export async function getMeta(key) { const x = await get(STORES.meta, key); return x?.value; }

export function genLocalId(prefix = 'local') {
  return `offline-${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function text(value) { return String(value ?? '').trim().toLowerCase(); }
function num(v) { return Number(v) || 0; }

// ── Medicines (POS / search cache) ──────────────────────────────────────────
// One row per medicine, or per medicine+batch when batch level data exists.
// Fed both by GET /medicines and GET /inventory so POS search always has
// up to date stock levels available offline.
export function normalizeMedicineRecord(row) {
  const med = row?.medicine && typeof row.medicine === 'object' ? row.medicine : row;
  const medicineId = String(med?._id || row?.medicineId || row?.medicine || '');
  const batchNumber = row?.batchNumber ?? med?.batchNumber ?? '';
  const localKey = `${medicineId}::${batchNumber || 'NO_BATCH'}`;
  // Backend uses different field names depending on the endpoint
  // (/medicines -> availableQty, /inventory -> currentStock). Accept both.
  const qty = row?.availableQty ?? row?.currentStock ?? med?.availableQty ?? med?.currentStock ?? 0;
  return {
    ...med,
    ...row,
    medicineId,
    _id: med?._id || medicineId,
    localKey,
    batchNumber,
    inventoryId: row?._id && row?._id !== medicineId ? row._id : (med?._id ? undefined : row?._id),
    availableQty: num(qty),
    currentStock: num(qty),
    sellingPrice: num(row?.sellingPrice ?? med?.sellingPrice),
    purchasePrice: num(row?.purchasePrice ?? med?.purchasePrice),
    salesUnits: row?.salesUnits || med?.salesUnits || [],
    commercialName: med?.commercialName || row?.commercialName || '',
    genericName: med?.genericName || row?.genericName || '',
    barcode: med?.barcode || row?.barcode || '',
    isActive: med?.isActive ?? row?.isActive ?? true,
  };
}

export async function saveMedicineResults(rows = []) {
  // Two very different kinds of calls write into this same cache slot
  // (keyed by medicineId + batch): plain catalog fetches (e.g. browsing the
  // Medicines page, or the /medicines part of the reference sync), which
  // carry NO stock number, and inventory/search-with-batches fetches, which
  // do. A catalog-only row used to blindly overwrite whatever was cached,
  // resetting a medicine's real stock back to 0 — which then made it
  // silently vanish from sales search (filtered out as "no stock"),
  // especially for items with no batch number (they share one cache slot
  // per medicine, so this collision was more likely for them). Merging
  // against whatever's already cached means a catalog-only row — which
  // never actually carried a stock number — can no longer wipe one out.
  const existingAll = await getAll(STORES.medicines);
  const existingByKey = new Map(existingAll.map(r => [r.localKey, r]));
  const normalized = rows.map(row => {
    const med = row?.medicine && typeof row.medicine === 'object' ? row.medicine : row;
    const id = String(med?._id || row?.medicineId || row?.medicine || '');
    const batch = row?.batchNumber || '';
    const localKey = `${id}::${batch || 'NO_BATCH'}`;
    const existing = existingByKey.get(localKey) || {};
    return normalizeMedicineRecord({ ...existing, ...row, medicineId: id, batchNumber: batch });
  }).filter(x => x.medicineId);
  await putMany(STORES.medicines, normalized);
  return normalized;
}

export async function upsertMedicineCacheRow(partial) {
  const rows = await getAll(STORES.medicines);
  const id = String(partial.medicineId || partial._id || '');
  const batch = partial.batchNumber || '';
  const localKey = `${id}::${batch || 'NO_BATCH'}`;
  const existing = rows.find(r => r.localKey === localKey) || {};
  const merged = normalizeMedicineRecord({ ...existing, ...partial, medicineId: id, batchNumber: batch });
  await put(STORES.medicines, merged);
  return merged;
}

export async function removeMedicineCacheByMedicineId(medicineId) {
  const rows = await getAllByIndex(STORES.medicines, 'medicineId', String(medicineId));
  for (const row of rows) await remove(STORES.medicines, row.localKey);
}

// A medicine's card (name/prices/salesUnits/...) can be cached as several
// rows in STORES.medicines — one per batch, plus the "no batch" catalog row.
// searchMedicines() actively hides the no-batch row once real batch rows
// exist, so updating only that row after a medicine-card edit leaves every
// batch row (the ones Sales can actually find) with stale info. This writes
// the given fields onto every row already cached for the medicine, or onto
// a fresh no-batch row if none exist yet.
export async function syncMedicineCatalogFields(medicineId, fields) {
  const id = String(medicineId);
  const rows = (await getAll(STORES.medicines)).filter(r => r.medicineId === id);
  if (!rows.length) {
    await upsertMedicineCacheRow({ ...fields, medicineId: id, batchNumber: '' });
    return;
  }
  for (const r of rows) {
    await upsertMedicineCacheRow({ ...fields, medicineId: id, batchNumber: r.batchNumber || '' });
  }
}

// inStockOnly (used by Sales): the "has stock" filter must run BEFORE the
// duplicate-hiding and the 30-result cut. Applied afterwards it dropped real
// items: a medicine with general (no-batch) stock was hidden just because
// some of its other batches existed at zero stock, and in-stock items could
// be pushed past the 30 limit by zero-stock matches.
export async function searchMedicines(q, { inStockOnly = false } = {}) {
  const query = text(q);
  if (!query) return [];
  const rows = await getAll(STORES.medicines);
  const terms = query.split(/\s+/).filter(Boolean);
  const matched = rows.filter(m => {
    if (m.isActive === false) return false;
    if (inStockOnly && !(num(m.availableQty) > 0)) return false;
    const hay = [m.commercialName, m.genericName, m.barcode, m.strength, m.drugForm, m.batchNumber]
      .map(text).join(' ');
    return terms.every(term => hay.includes(term));
  });
  // If batch-level inventory exists, do not also show the duplicate base medicine row.
  const batchMedicineIds = new Set(matched.filter(x => x.batchNumber).map(x => x.medicineId));
  const visible = matched.filter(x => !(!x.batchNumber && batchMedicineIds.has(x.medicineId)));
  return visible.sort((a, b) => {
    const aa = text(a.commercialName).startsWith(query) ? 0 : 1;
    const bb = text(b.commercialName).startsWith(query) ? 0 : 1;
    return aa - bb || text(a.commercialName).localeCompare(text(b.commercialName));
  }).slice(0, 30);
}

// Unique medicine "cards" (one row per medicine, ignoring batches) — used by
// the Medicines management page list/create/update/delete.
export async function getMedicineCatalog() {
  const rows = await getAll(STORES.medicines);
  const byId = new Map();
  for (const row of rows) {
    const prev = byId.get(row.medicineId);
    // Prefer the row without a batch number as the representative card;
    // otherwise keep the first one seen.
    if (!prev || (prev.batchNumber && !row.batchNumber)) byId.set(row.medicineId, row);
  }
  return [...byId.values()];
}

// Resolves a purchase/sale unit (e.g. "box") to how many base units
// (e.g. "tablet") it represents, using the medicine's salesUnits table.
export async function getConversionToBase(medicineId, unit) {
  if (!unit) return 1;
  const rows = await getAll(STORES.medicines);
  const id = String(medicineId);
  const row = rows.find(x => x.medicineId === id && (x.salesUnits || []).length) || rows.find(x => x.medicineId === id);
  if (!row || unit === row.baseUnit) return 1;
  return Number((row.salesUnits || []).find(u => u.unitKey === unit)?.conversionToBase || 1);
}

export async function decrementMedicine(medicineId, batchNumber, quantity, unit) {
  const rows = await getAll(STORES.medicines);
  const id = String(medicineId);
  // Exactly the medicine + batch asked for. Falling back to "any batch of this
  // medicine" took the stock from a different batch than the one the server
  // takes it from, so the two numbers drifted apart.
  const row = rows.find(x => x.medicineId === id && String(x.batchNumber || '') === String(batchNumber || ''));
  if (!row) return;
  const conversion = unit && unit !== row.baseUnit
    ? Number((row.salesUnits || []).find(u => u.unitKey === unit)?.conversionToBase || 1)
    : 1;
  row.availableQty = Math.max(0, num(row.availableQty) - num(quantity) * conversion);
  row.currentStock = row.availableQty;
  await put(STORES.medicines, row);
}

export async function incrementMedicine(medicineId, batchNumber, quantity, unit, extra = {}) {
  const rows = await getAll(STORES.medicines);
  const id = String(medicineId);
  let row = rows.find(x => x.medicineId === id && String(x.batchNumber || '') === String(batchNumber || ''));
  const anyRowForMedicine = row || rows.find(x => x.medicineId === id);
  // (A stock-in WITHOUT a batch number used to be added onto whatever batch row
  // happened to exist, inflating that batch in Sales search while the real
  // no-batch stock never showed up. It gets its own row like the server's.)
  const conversion = unit && unit !== (row || anyRowForMedicine)?.baseUnit
    ? Number(((row || anyRowForMedicine)?.salesUnits || []).find(u => u.unitKey === unit)?.conversionToBase || 1)
    : 1;
  if (!row) {
    // No cached row for this medicine+batch at all — e.g. a brand-new
    // medicine's very first stock-in, or a new batch of an existing one.
    // Without creating it here it would silently never show up in Sales
    // search (which only reads this store), even though the stock-in
    // succeeded and the Inventory page shows it fine.
    //
    // The new row must carry the WHOLE medicine card (barcode, strength, drug
    // form, category, ...), not just a few fields: Sales search matches on
    // those, so a half-filled row was found by name but not by barcode /
    // strength, and showed up without its details.
    const card = { ...(anyRowForMedicine || {}) };
    delete card.localKey; delete card.inventoryId; delete card.batchNumber; delete card.expiryDate;
    delete card.productionDate; delete card.availableQty; delete card.currentStock; delete card.__st;
    delete card.offline; delete card.syncStatus;
    row = normalizeMedicineRecord({
      ...card,
      medicineId: id,
      batchNumber: batchNumber || '',
      commercialName: extra.medicineName || anyRowForMedicine?.commercialName || '',
      genericName: anyRowForMedicine?.genericName || '',
      baseUnit: anyRowForMedicine?.baseUnit || extra.baseUnit || unit || 'strip',
      salesUnits: anyRowForMedicine?.salesUnits || [],
      purchasePrice: extra.purchasePrice ?? anyRowForMedicine?.purchasePrice ?? 0,
      sellingPrice: extra.sellingPrice ?? anyRowForMedicine?.sellingPrice ?? 0,
      expiryDate: extra.expiryDate || null,
      productionDate: extra.productionDate || null,
      availableQty: 0,
      isActive: anyRowForMedicine?.isActive ?? true,
    });
  }
  row.availableQty = num(row.availableQty) + num(quantity) * conversion;
  row.currentStock = row.availableQty;
  if (extra.expiryDate) row.expiryDate = extra.expiryDate;
  if (extra.productionDate) row.productionDate = extra.productionDate;
  if (extra.purchasePrice) row.purchasePrice = num(extra.purchasePrice);
  if (extra.sellingPrice) row.sellingPrice = num(extra.sellingPrice);
  await put(STORES.medicines, row);
}

// ── Inventory (InventorySummary rows for the Inventory page) ───────────────
// Saves rows that came FROM THE SERVER. A stock-in/purchase-receive for a
// brand-new batch creates a temporary local row with an "offline-inv-…" id
// (see applyInventoryMovement) — even when the request actually succeeded
// online. Once the real server row for the same medicine+batch arrives, that
// temporary row must go, otherwise the batch shows up twice in the cached
// Inventory list (and its stock is counted twice in the offline overview).
export async function saveInventoryRows(rows = []) {
  const normalized = rows.map(r => normalizeInventoryRow(r)).filter(x => x._id);
  await putMany(STORES.inventory, normalized);
  const serverKeys = new Set(normalized.filter(r => !String(r._id).startsWith('offline-'))
    .map(r => `${r.medicineId}::${r.batchNumber || ''}`));
  if (serverKeys.size) {
    const local = await getAll(STORES.inventory);
    for (const r of local) {
      if (String(r._id).startsWith('offline-') && serverKeys.has(`${r.medicineId}::${r.batchNumber || ''}`)) {
        await remove(STORES.inventory, r._id);
      }
    }
  }
  return normalized;
}

// Full-sync only: makes the local inventory cache an exact copy of the
// complete server list. Rows the server no longer has (deleted on another
// device, or temporary offline rows whose queued request has now been
// applied) are dropped — saveInventoryRows() alone only ever adds/updates,
// so they used to stay forever as "ghost" stock. Callers must make sure the
// local queue is drained and `rows` is the COMPLETE list before calling this.
export async function replaceInventoryRows(rows = []) {
  const normalized = rows.map(r => normalizeInventoryRow(r)).filter(x => x._id);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORES.inventory, 'readwrite');
    const s = t.objectStore(STORES.inventory);
    s.clear();
    normalized.forEach(row => s.put(row));
    t.oncomplete = () => resolve(normalized);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('IndexedDB transaction aborted'));
  });
}

// Same idea for the POS/search cache: batch-level rows only come from
// /inventory, so once the full inventory list is known, a cached batch row the
// server doesn't have any more (and that has no queued change behind it) is
// stale stock that Sales search would still happily sell.
export async function pruneMedicineBatchRows(serverInventoryRows = []) {
  const keep = new Set(serverInventoryRows.map(r => {
    const med = r?.medicine && typeof r.medicine === 'object' ? r.medicine : null;
    const id = String(med?._id || r?.medicine || r?.medicineId || '');
    return `${id}::${r?.batchNumber || 'NO_BATCH'}`;
  }));
  const local = await getAll(STORES.medicines);
  for (const r of local) {
    if (r.batchNumber && !keep.has(r.localKey) && !String(r.medicineId).startsWith('offline-')) {
      await remove(STORES.medicines, r.localKey);
    }
  }
}

// Stock in base units for exactly this medicine + batch. null = nothing cached
// for the medicine at all (unknown → don't block). If the medicine is known but
// has no row for this batch, the server has no summary for it either, so the
// answer is 0 (that's what the server's stock-out check sees too).
export async function getLocalStockBase(medicineId, batchNumber) {
  const id = String(medicineId);
  const rows = (await getAll(STORES.inventory)).filter(x => x.medicineId === id);
  if (!rows.length) return null;
  const row = rows.find(x => String(x.batchNumber || '') === String(batchNumber || ''));
  return row ? num(row.currentStock) : 0;
}

// Take a deleted / renamed batch out of the Sales search cache. The no-batch
// row doubles as the medicine's catalog card, so that one is only zeroed.
export async function dropMedicineBatchCache(medicineId, batchNumber) {
  const id = String(medicineId);
  const batch = batchNumber || '';
  const rows = await getAllByIndex(STORES.medicines, 'medicineId', id);
  const row = rows.find(r => String(r.batchNumber || '') === String(batch));
  if (!row) return;
  if (!batch) { await put(STORES.medicines, { ...row, availableQty: 0, currentStock: 0 }); return; }
  await remove(STORES.medicines, row.localKey);
}

export function normalizeInventoryRow(row) {
  const med = row?.medicine && typeof row.medicine === 'object' ? row.medicine : null;
  const medicineId = String(med?._id || row?.medicine || row?.medicineId || '');
  return {
    ...row,
    _id: row._id || genLocalId('inv'),
    medicineId,
    medicine: med || row.medicine,
    currentStock: num(row.currentStock ?? row.availableQty),
    purchasePrice: num(row.purchasePrice),
    sellingPrice: num(row.sellingPrice),
    batchNumber: row.batchNumber || '',
    expiryDate: row.expiryDate || null,
    productionDate: row.productionDate || null,
    notes: row.notes || '',
  };
}

export async function getInventoryAll() { return getAll(STORES.inventory); }
export async function getInventoryById(id) { return get(STORES.inventory, id); }
export async function upsertInventoryRow(row) { return put(STORES.inventory, normalizeInventoryRow(row)); }
export async function removeInventoryRow(id) { return remove(STORES.inventory, id); }

// Applies a stock movement to the local inventory (creating a batch row if
// none exists yet) and mirrors the change onto the POS/search cache so the
// two stay consistent while offline.
// direction: +1 for stock in / purchase receive, -1 for stock out / sale.
export async function applyInventoryMovement({ medicineId, medicineName, batchNumber, quantity, unit, direction = 1,
  purchasePrice, sellingPrice, expiryDate, productionDate, notes }) {
  const conversion = await getConversionToBase(medicineId, unit);
  const baseQty = num(quantity) * conversion;
  const delta = direction >= 0 ? baseQty : -baseQty;

  const rows = await getAll(STORES.inventory);
  const id = String(medicineId);
  let row = rows.find(x => x.medicineId === id && String(x.batchNumber || '') === String(batchNumber || ''));
  if (!row && direction >= 0) {
    row = normalizeInventoryRow({
      _id: genLocalId('inv'),
      origBatchNumber: batchNumber || '', // lets sync find the real row even if the batch is renamed offline
      medicineId: id,
      medicine: { _id: id, commercialName: medicineName },
      batchNumber: batchNumber || '',
      currentStock: 0,
      totalIn: 0,
      totalOut: 0,
      purchasePrice: purchasePrice || 0,
      sellingPrice: sellingPrice || 0,
      expiryDate: expiryDate || null,
      productionDate: productionDate || null,
      notes: notes || '',
    });
  }
  if (row) {
    row.currentStock = Math.max(0, num(row.currentStock) + delta);
    row.totalIn = num(row.totalIn) + (direction >= 0 ? baseQty : 0);
    row.totalOut = num(row.totalOut) + (direction < 0 ? baseQty : 0);
    if (purchasePrice) row.purchasePrice = num(purchasePrice);
    if (sellingPrice) row.sellingPrice = num(sellingPrice);
    if (expiryDate) row.expiryDate = expiryDate;
    if (productionDate) row.productionDate = productionDate;
    row.updatedAt = new Date().toISOString(); // most recently changed batch sorts first, like the server
    await put(STORES.inventory, row);
  }

  if (direction >= 0) await incrementMedicine(medicineId, batchNumber, quantity, unit, { medicineName, purchasePrice, sellingPrice, expiryDate, productionDate });
  else await decrementMedicine(medicineId, batchNumber, quantity, unit);

  return row;
}

// ── Inventory transactions log (Stock In / Stock Out / Receive) ────────────
export async function addInventoryTx(entry) {
  const row = { ...entry, createdAt: entry.createdAt || Date.now() };
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORES.inventoryTx, 'readwrite');
    const req = t.objectStore(STORES.inventoryTx).add(row);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
export async function getInventoryTx() {
  const rows = await getAll(STORES.inventoryTx);
  return rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

// ── Customers / Suppliers ───────────────────────────────────────────────────
export async function saveCustomers(rows = []) { await putMany(STORES.customers, rows.filter(x => x?._id)); }
export async function searchCustomers(q) {
  const query = text(q);
  if (!query) return [];
  const rows = await getAll(STORES.customers);
  return rows.filter(c => `${text(c.name)} ${text(c.phone)}`.includes(query)).slice(0, 8);
}

export async function saveSuppliers(rows = []) { await putMany(STORES.suppliers, rows.filter(x => x?._id)); }
export async function searchSuppliers(q) {
  const query = text(q);
  if (!query) return [];
  const rows = await getAll(STORES.suppliers);
  return rows.filter(s => `${text(s.name)} ${text(s.phone)}`.includes(query)).slice(0, 8);
}

// ── Categories (used by the medicine/quick-add-medicine forms) ─────────────
export async function saveCategories(rows = []) { await putMany(STORES.categories, rows.filter(x => x?._id)); }
export async function getCategories() { return getAll(STORES.categories); }

// ── Sales ────────────────────────────────────────────────────────────────
export async function saveSale(sale) { await put(STORES.sales, sale); return sale; }
export async function getSales() { return getAll(STORES.sales); }
export async function saveSales(rows = []) { await putMany(STORES.sales, rows.filter(x => x?._id)); }

// ── Sale Returns ─────────────────────────────────────────────────────────
export async function getSaleReturns() { return getAll(STORES.saleReturns); }
export async function saveSaleReturns(rows = []) { await putMany(STORES.saleReturns, rows.filter(x => x?._id)); }
export async function saveSaleReturn(row) { await put(STORES.saleReturns, row); return row; }

// ── Purchases ────────────────────────────────────────────────────────────
export async function savePurchase(purchase) { await put(STORES.purchases, purchase); return purchase; }
export async function getPurchases() { return getAll(STORES.purchases); }
export async function savePurchases(rows = []) { await putMany(STORES.purchases, rows.filter(x => x?._id)); }

// ── Expenses ─────────────────────────────────────────────────────────────
export async function saveExpense(expense) { await put(STORES.expenses, expense); return expense; }
export async function getExpenses() { return getAll(STORES.expenses); }
export async function removeExpense(id) { return remove(STORES.expenses, id); }

// ── Accounting ledger payments (customer / supplier) ───────────────────────
export async function addLedgerPayment(parentType, parentId, data) {
  const row = { parentType, parentId: String(parentId), ...data, createdAt: Date.now(), syncStatus: 'pending' };
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORES.payments, 'readwrite');
    const req = t.objectStore(STORES.payments).add(row);
    req.onsuccess = () => resolve({ ...row, id: req.result });
    req.onerror = () => reject(req.error);
  });
}
export async function getLedgerPayments(parentType, parentId) {
  const rows = await getAllByIndex(STORES.payments, 'parentId', String(parentId));
  return rows.filter(r => r.parentType === parentType).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}
export async function updateLedgerPayment(id, data) {
  const row = await get(STORES.payments, id);
  if (!row) return null;
  const updated = { ...row, ...data, syncStatus: 'pending' };
  await put(STORES.payments, updated);
  return updated;
}
export async function removeLedgerPayment(id) { return remove(STORES.payments, id); }

// ── Settings cache ──────────────────────────────────────────────────────────
export async function saveSettingsCache(data) { return put(STORES.settings, { key: 'settings', value: data }); }
export async function getSettingsCache() { const r = await get(STORES.settings, 'settings'); return r?.value || {}; }

// ── Sync queue (offline write operations waiting to reach the server) ──────
export async function enqueue(operation) {
  const row = { ...operation, createdAt: Date.now() };
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORES.queue, 'readwrite');
    const request = t.objectStore(STORES.queue).add(row);
    request.onsuccess = () => {
      resolve(request.result);
      // lets the "N changes waiting" bar update immediately
      try { window.dispatchEvent(new Event('pharma-queue-changed')); } catch { /* not in a browser */ }
    };
    request.onerror = () => reject(request.error);
  });
}
export async function getQueue() { return getAll(STORES.queue); }
export async function removeQueue(id) { return remove(STORES.queue, id); }
export async function updateQueueItem(id, data) { return put(STORES.queue, { ...data, id }); }
export async function getQueueCount() { const rows = await getQueue(); return rows.length; }
// Queue items that are still waiting to be pushed (i.e. not ones the server
// already rejected with a real error). While any of these exist, the local
// cache holds changes the server doesn't know about yet — pulling server data
// over it would wipe those changes from the screen.
export async function hasPendingQueue() {
  const rows = await getQueue();
  return rows.some(r => !r.lastError);
}

// Removes every queued operation that touches a given id (its create, and
// any edit/approve/receive/cancel/payment queued against it) — used when a
// record that was only ever created offline (never reached the server) gets
// permanently deleted, so a leftover queued item can't resurrect it later.
export async function purgeQueueReferencing(idStr) {
  if (!idStr) return;
  const rows = await getQueue();
  for (const row of rows) {
    const haystack = `${row.url || ''} ${JSON.stringify(row.data || {})}`;
    if (haystack.includes(idStr)) await removeQueue(row.id);
  }
}

// ── Generic API cache (offline/generic.js) ─────────────────────────────────
export async function apiCacheGet(key) { const r = await get(STORES.apiCache, key); return r ? r.value : undefined; }
export async function apiCachePut(key, value) { return put(STORES.apiCache, { key, value, cachedAt: Date.now() }); }
export async function apiCacheEntries(prefix) {
  const rows = await getAll(STORES.apiCache);
  return rows.filter(r => r.key.startsWith(prefix));
}

export default { openDB };
